/**
 * Stringify an unknown error into a UI-safe message.
 *
 * The naive `String(err)` falls through to `[object Object]` /
 * `[object Event]` for browser-native error carriers (a `WebSocket.onerror`
 * callback receives a bare DOM `Event`, not an `Error`). This helper
 * recognises the common shapes:
 *   - `Error` instances → `.message`
 *   - plain `string` → returned as-is
 *   - `ErrorEvent` → `.message` (when populated)
 *   - `CloseEvent` → `.reason` or `"WebSocket closed (code N)"`
 *   - any other DOM `Event` → generic `"WebSocket error"` (browsers
 *     deliberately suppress detail on cross-origin / network failures)
 *   - everything else → `String(err)` as a last resort
 *
 * Used by `useStationStore` and by tests that need stable error strings.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (typeof Event !== 'undefined' && err instanceof Event) {
    if (
      typeof ErrorEvent !== 'undefined' &&
      err instanceof ErrorEvent &&
      err.message
    ) {
      return err.message;
    }
    if (typeof CloseEvent !== 'undefined' && err instanceof CloseEvent) {
      return err.reason || `WebSocket closed (code ${err.code})`;
    }
    return 'WebSocket error';
  }
  return String(err);
}
